import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Container from "@components/Container/Container";
import { socialLinks } from "@components/Footer/constant";
import { faFacebookF, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type UserInfoType, useUserInfo } from "@hooks/useUserInfo";
import useOKTAUserStore from "@store/useOKTAUserStore";
import { sectionNavigationItems } from "@util/constant";
import cx from "classnames";

import LoginTrigger from "./LoginTrigger/LoginTrigger";
import Logo from "./Logo/Logo";
import LogoMasthead from "./LogoMasthead/LogoMasthead";
import Navigation from "./Navigation/Navigation";
import SearchTrigger from "./SearchTrigger/SearchTrigger";
import SideMenu from "./SideMenu/SideMenu";
import SubscribeTrigger from "./SubscribeTrigger/SubscribeTrigger";
import ToggleSideMenuButton from "./ToggleSideMenuButton/ToggleSideMenuButton";
import UserInfo from "./UserInfo/UserInfo";
import { useHeaderState } from "./helpers";
import SocialMediaLinksPopover from "./SocialMediaLinksPopover";

import styles from "./Header.module.scss";

export type HeaderProps = {
  /**
   * @description for prefixing custom attributes as component might be used by different scope
   * @example data-testid={`${prefix}-xxx`}
   */
  prefix?: string;
  children?: ReactNode;
  mastheadTitle?: string;
  mastheadCustomElement?: React.ReactElement;
};

export type SocialLinkConfig = {
  /**
   * @description icon image of the social link
   */
  src: string;
  link?: string;
};

export default function Header({
  prefix = "homepage",
  mastheadTitle = "",
  mastheadCustomElement,
}: HeaderProps) {
  const [fullSocialLinksVisiblity, setFullSocialLinksVisiblity] =
    useState(false);
  const { userInfo } = useUserInfo();
  const fullSocialLinksRef = useRef<HTMLDivElement>(null);

  // TODO change to OKTA user info
  // check user typing in useOKTAUserInfo.ts
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const userDisplayName = OKTAUserInfo?.display_name || OKTAUserInfo?.loginid;
  const socialLinkClass =
    "w-8 h-8 flex justify-center items-center cursor-pointer opacity-100 hover:opacity-75 bg-black rounded-pill m-[3px]";

  const handleToggleFullSocialLinks = (e: SyntheticEvent) => {
    setFullSocialLinksVisiblity((currvisibility) => !currvisibility);
    e.preventDefault();
  };

  const { showLowerNavBar, isHeaderStuck, headerRef } = useHeaderState();

  useEffect(() => {
    window.onclick = (event: MouseEvent) => {
      const target = event.target as
        | HTMLDivElement
        | HTMLInputElement
        | HTMLParagraphElement
        | HTMLButtonElement
        | HTMLHeadingElement;

      if (
        target?.contains(fullSocialLinksRef.current) &&
        target !== fullSocialLinksRef.current
      ) {
        setFullSocialLinksVisiblity(() => false);
      }
    };
  }, []);

  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const toggleSideMenu = () => {
    setIsSideMenuOpen((prev) => !prev);
  };

  return (
    <>
      {/* logo masthead */}
      <Container rootClassName="w-full flex-none max-lg:hidden py-6 relative">
        {mastheadTitle && mastheadTitle in sectionNavigationItems ? (
          <>
            <LogoMasthead
              rootClassName="absolute"
              imgWidth={190}
              imgHeight={19}
            />
            {mastheadCustomElement ?? (
              <h2 className="text-center font-playfair text-8xl uppercase">
                <Link
                  to={`/${mastheadTitle}`}
                  rel="noopener noreferer"
                  reloadDocument
                >
                  {sectionNavigationItems?.[mastheadTitle]?.label}
                </Link>
              </h2>
            )}
          </>
        ) : (
          <LogoMasthead />
        )}
      </Container>

      <header
        ref={headerRef}
        data-testid="navigation-bar"
        className={cx(
          styles.componentHeader,
          "sticky left-0 top-[-1px] z-20 w-full flex-none"
        )}
      >
        {/* uppper */}
        <div
          className={cx("relative z-10 flex w-full items-center bg-gray-850", {
            "lg:py-0": !isHeaderStuck,
          })}
        >
          <Container rootClassName="flex items-center justify-between gap-4">
            <div className="flex items-center justify-start">
              <div
                className={cx("max-lg:hidden", {
                  hidden: isHeaderStuck,
                })}
              >
                <ToggleSideMenuButton onClick={toggleSideMenu} />
              </div>
              <div
                className={cx(
                  {
                    "lg:hidden": !isHeaderStuck,
                  },
                  "py-3"
                )}
              >
                <Logo
                  style={{ height: "30px" }}
                  data-testid={`${prefix}-logo`}
                  alt="The Business Times"
                />
              </div>
              <div
                className={cx("max-lg:hidden", {
                  hidden: isHeaderStuck,
                })}
              >
                {/* Separator */}
                <div className="block h-[30px] w-[0.5px] bg-white"></div>
              </div>
              <div
                className={cx("max-lg:hidden", {
                  hidden: isHeaderStuck,
                })}
              >
                <Navigation />
              </div>
            </div>
            <div className="flex items-center justify-start">
              {!userDisplayName ? (
                <div
                  className={cx("last-of-type:mr-0", "max-lg:hidden", {
                    hidden: isHeaderStuck,
                  })}
                >
                  <SubscribeTrigger />
                </div>
              ) : null}
              <div
                className={cx("last-of-type:mr-0", "max-lg:hidden", {
                  hidden: isHeaderStuck,
                })}
              >
                {userDisplayName ? (
                  <UserInfo
                    rootClassName="text-white"
                    userInfo={
                      {
                        ...userInfo,
                        lastname: userDisplayName,
                        loginId: OKTAUserInfo?.loginid,
                        sub: OKTAUserInfo?.reguserstatus,
                      } as UserInfoType
                    }
                  />
                ) : (
                  <LoginTrigger white />
                )}
              </div>
              <div className={"last-of-type:mr-0"}>
                <SearchTrigger />
              </div>
              <div
                className={cx("mr-2 last-of-type:mr-0", {
                  "lg:hidden": !isHeaderStuck,
                })}
              >
                <ToggleSideMenuButton onClick={toggleSideMenu} />
              </div>
            </div>
          </Container>
        </div>
        {/* lower */}
        <div
          className={cx(
            "relative flex w-full items-center justify-center bg-gray-150 py-2 duration-300",
            {
              "lg:hidden": !isHeaderStuck,
            },
            !showLowerNavBar && "-translate-y-[54px] lg:-translate-y-0"
          )}
        >
          <div className="container flex justify-between px-3 text-white">
            <div className="flex items-center justify-start">
              <a
                className={socialLinkClass}
                href={socialLinks.fbBlack?.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon
                  icon={faFacebookF}
                  title={socialLinks.fbBlack?.alt}
                />
              </a>
              <a
                className={socialLinkClass}
                href={socialLinks.twitter?.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon
                  icon={faXTwitter}
                  title={socialLinks.twitter?.alt}
                />
              </a>
              <a
                className={socialLinkClass}
                href="#"
                target="_blank"
                rel="noopener"
                onClick={handleToggleFullSocialLinks}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} title="View more" />
              </a>
              {fullSocialLinksVisiblity ? (
                <SocialMediaLinksPopover
                  ref={fullSocialLinksRef}
                  rootClassName="absolute top-12"
                />
              ) : null}
            </div>
            <div className="flex items-center">
              {!userDisplayName ? (
                <div>
                  <SubscribeTrigger />
                </div>
              ) : null}
              <div>
                {userDisplayName ? (
                  <UserInfo
                    rootClassName={styles.dark}
                    userInfo={
                      {
                        ...userInfo,
                        lastname: userDisplayName,
                        loginId: OKTAUserInfo?.loginid,
                        sub: OKTAUserInfo?.reguserstatus,
                      } as UserInfoType
                    }
                  />
                ) : (
                  <LoginTrigger title="Login" />
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <SideMenu
        isSideMenuOpen={isSideMenuOpen}
        setIsSideMenuOpen={setIsSideMenuOpen}
      />
    </>
  );
}
